import type { Pt } from './geometry.ts';

/**
 * Turns a freehand stroke into a clean run: snaps it onto the roofline edge it was traced
 * along, then reduces it to corner points. Pure and Skia-free; the edge field is built
 * from the photo (and the on-device depth map, when there is one) in edgeField.ts.
 */

/** Per-pixel edge evidence at reduced resolution, normalized so ~1 is a strong edge. */
export interface EdgeField {
  width: number;
  height: number;
  /** Field px per image px. */
  scale: number;
  /** Unit gradient direction × strength, photo luminance. */
  gx: Float32Array;
  gy: Float32Array;
  /** Same for the depth map (silhouettes: roof against sky, eave against background); zero without depth. */
  dx: Float32Array;
  dy: Float32Array;
}

export interface FitOptions {
  /** How far (image px) the stroke may move sideways to reach an edge. */
  radius: number;
  /** Max deviation (image px) when reducing the snapped path to corner points. */
  tolerance: number;
}

export interface FitResult {
  points: Pt[];
  /** True when the stroke was moved onto image edges; false when it was kept as drawn. */
  snapped: boolean;
}

/** Sideways offsets tried per sample (each side). */
const STEPS = 10;
/** Depth edges count more than color edges: a silhouette is almost always the roof edge. */
const DEPTH_WEIGHT = 1.4;
/** Below this mean edge support the photo has no clear line there; keep the stroke. */
const MIN_SUPPORT = 0.22;
/** Cost of staying far from the stroke (keeps the fit near what was drawn). */
const STAY = 0.18;
/**
 * Preference for the lower of two comparable edges: the track hangs under the roof edge, on
 * the fascia, so a stroke traced along the roof's top edge (against the sky) should land on
 * the trim just below it rather than on the shingles.
 */
const DOWN = 0.35;
/** Cost of jumping sideways between neighbouring samples (keeps the fit on one line). */
const SMOOTH = 6;
/** Cost of switching edge polarity between neighbouring samples. */
const FLIP = 5;
/**
 * Sobel magnitude (4 × contrast for a sharp step) treated as a full-strength edge at least:
 * ~0.08 luminance contrast for the photo, a modest depth step for the depth map.
 */
const PHOTO_FLOOR = 0.32;
const DEPTH_FLOOR = 0.12;

export function fitStroke(stroke: Pt[], field: EdgeField | null, opts: FitOptions): FitResult {
  const step = Math.max(1.5, opts.radius / 5);
  const samples = smooth(resample(dedupe(stroke), step), 2);
  if (samples.length < 2) return { points: samples, snapped: false };
  if (!field || samples.length < 3) return { points: simplify(samples, opts.tolerance), snapped: false };

  const first = snapToEdges(samples, field, opts.radius);
  if (!first) return { points: simplify(samples, opts.tolerance), snapped: false };
  // Refine from the first fit: reaches edges just past the first radius where the hand drifted.
  const refined = snapToEdges(smooth(first, 2), field, opts.radius * 0.6) ?? first;
  return { points: simplify(smooth(refined, 1), opts.tolerance), snapped: true };
}

/**
 * Moves every sample along its normal onto the strongest nearby edge that runs the same way
 * as the stroke, choosing all offsets together (Viterbi) so the result stays one coherent
 * line instead of hopping between unrelated edges. Each state also carries the edge's
 * polarity (darker above vs. lighter above): a real edge keeps its polarity along its
 * length, so switching costs extra, which stops the fit zig-zagging between the top and
 * bottom of the fascia.
 */
export function snapToEdges(samples: Pt[], field: EdgeField, radius: number): Pt[] | null {
  const n = samples.length;
  const K = STEPS * 2 + 1;
  const S = K * 2; // offset × polarity
  const normals = samples.map((_, i) => normalAt(samples, i));
  const support = new Float32Array(n * S);
  for (let i = 0; i < n; i++) {
    const p = samples[i];
    const nm = normals[i];
    for (let k = 0; k < K; k++) {
      const off = ((k - STEPS) / STEPS) * radius;
      const [photo, depth] = edgeSupport(field, p.x + nm.x * off, p.y + nm.y * off, nm.x, nm.y);
      support[i * S + k] = Math.min(1.5, Math.max(0, photo) + DEPTH_WEIGHT * depth);
      support[i * S + K + k] = Math.min(1.5, Math.max(0, -photo) + DEPTH_WEIGHT * depth);
    }
  }

  const unary = (i: number, st: number) => {
    const u = ((st % K) - STEPS) / STEPS;
    return -support[i * S + st] + STAY * u * u - DOWN * u * normals[i].y;
  };
  const cost = new Float32Array(n * S);
  const from = new Int16Array(n * S);
  for (let st = 0; st < S; st++) cost[st] = unary(0, st);
  for (let i = 1; i < n; i++) {
    for (let st = 0; st < S; st++) {
      let best = Infinity;
      let arg = 0;
      for (let j = 0; j < S; j++) {
        const d = ((st % K) - (j % K)) / STEPS;
        const flip = st < K !== j < K ? FLIP : 0;
        const c = cost[(i - 1) * S + j] + SMOOTH * d * d + flip;
        if (c < best) {
          best = c;
          arg = j;
        }
      }
      cost[i * S + st] = best + unary(i, st);
      from[i * S + st] = arg;
    }
  }

  let st = 0;
  for (let j = 1; j < S; j++) if (cost[(n - 1) * S + j] < cost[(n - 1) * S + st]) st = j;
  const path = new Array<number>(n);
  for (let i = n - 1; i >= 0; i--) {
    path[i] = st;
    st = from[i * S + st];
  }

  let total = 0;
  for (let i = 0; i < n; i++) total += support[i * S + path[i]];
  if (total / n < MIN_SUPPORT) return null;

  return samples.map((p, i) => {
    const off = (((path[i] % K) - STEPS) / STEPS) * radius;
    return { x: p.x + normals[i].x * off, y: p.y + normals[i].y * off };
  });
}

/**
 * Edge strength at an image point, counting only edges that run along the stroke: the
 * photo's signed (polarity) component and the depth map's unsigned one.
 */
function edgeSupport(f: EdgeField, x: number, y: number, nx: number, ny: number): [number, number] {
  const fx = Math.round(x * f.scale);
  const fy = Math.round(y * f.scale);
  if (fx < 0 || fy < 0 || fx >= f.width || fy >= f.height) return [0, 0];
  const i = fy * f.width + fx;
  // An edge along the stroke has its gradient across it, i.e. parallel to the normal.
  return [f.gx[i] * nx + f.gy[i] * ny, Math.abs(f.dx[i] * nx + f.dy[i] * ny)];
}

export function normalAt(pts: Pt[], i: number): Pt {
  const a = pts[Math.max(0, i - 2)];
  const b = pts[Math.min(pts.length - 1, i + 2)];
  const tx = b.x - a.x;
  const ty = b.y - a.y;
  const len = Math.hypot(tx, ty) || 1;
  return { x: -ty / len, y: tx / len };
}

export function dedupe(pts: Pt[]): Pt[] {
  return pts.filter((p, i) => i === 0 || p.x !== pts[i - 1].x || p.y !== pts[i - 1].y);
}

/** Points every `step` px along the polyline (plus its last point). */
export function resample(pts: Pt[], step: number): Pt[] {
  if (pts.length < 2) return pts.slice();
  const out: Pt[] = [pts[0]];
  let carry = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    let s = step - carry;
    while (s <= seg) {
      out.push({ x: a.x + ((b.x - a.x) * s) / seg, y: a.y + ((b.y - a.y) * s) / seg });
      s += step;
    }
    carry = seg - (s - step);
  }
  const last = pts[pts.length - 1];
  const tail = out[out.length - 1];
  if (Math.hypot(last.x - tail.x, last.y - tail.y) > step * 0.25) out.push(last);
  return out;
}

/** Moving-average smoothing that keeps both ends fixed (removes hand jitter). */
export function smooth(pts: Pt[], radius: number): Pt[] {
  if (pts.length < 3) return pts;
  return pts.map((p, i) => {
    if (i === 0 || i === pts.length - 1) return p;
    const r = Math.min(radius, i, pts.length - 1 - i);
    let x = 0;
    let y = 0;
    for (let j = i - r; j <= i + r; j++) {
      x += pts[j].x;
      y += pts[j].y;
    }
    return { x: x / (2 * r + 1), y: y / (2 * r + 1) };
  });
}

/** Ramer–Douglas–Peucker: keeps the corners, drops points within `tolerance` of the line. */
export function simplify(pts: Pt[], tolerance: number): Pt[] {
  if (pts.length < 3) return pts.slice();
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let worst = -1;
    let worstD = tolerance;
    for (let i = a + 1; i < b; i++) {
      const d = distToLine(pts[i], pts[a], pts[b]);
      if (d > worstD) {
        worstD = d;
        worst = i;
      }
    }
    if (worst >= 0) {
      keep[worst] = 1;
      stack.push([a, worst], [worst, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

export function distToLine(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return Math.hypot(p.x - a.x, p.y - a.y);
  return Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / len;
}

/* ------------------------------------------------------------------ edge field */

/**
 * Edge field from luminance rasters (0..1, `width`×`height`) of the photo and, optionally,
 * the depth map resampled to the same size. `imageW` is the scene width the field maps to.
 */
export function buildEdgeField(lum: Float32Array, depth: Float32Array | null, width: number, height: number, imageW: number): EdgeField {
  const [gx, gy] = gradients(blur(lum, width, height, 1), width, height, 0.9, PHOTO_FLOOR);
  let dx: Float32Array = new Float32Array(width * height);
  let dy: Float32Array = new Float32Array(width * height);
  // The depth map is soft and low-res; blur more so its edges don't stair-step.
  if (depth) [dx, dy] = gradients(blur(depth, width, height, 2), width, height, 0.97, DEPTH_FLOOR);
  return { width, height, scale: width / imageW, gx, gy, dx, dy };
}

/** Separable 3×3 box blur, `passes` times (≈ Gaussian). Leaves `src` untouched. */
function blur(src: Float32Array, w: number, h: number, passes: number): Float32Array {
  let cur = src;
  const tmp = new Float32Array(w * h);
  for (let p = 0; p < passes; p++) {
    const out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
      const row = y * w;
      for (let x = 0; x < w; x++) {
        tmp[row + x] = (cur[row + Math.max(0, x - 1)] + cur[row + x] + cur[row + Math.min(w - 1, x + 1)]) / 3;
      }
    }
    for (let y = 0; y < h; y++) {
      const up = Math.max(0, y - 1) * w;
      const row = y * w;
      const dn = Math.min(h - 1, y + 1) * w;
      for (let x = 0; x < w; x++) out[row + x] = (tmp[up + x] + tmp[row + x] + tmp[dn + x]) / 3;
    }
    cur = out;
  }
  return cur;
}

/**
 * Sobel gradient as unit direction × strength, with strength scaled so the given percentile
 * of edge magnitudes is 1 (works the same on dim and contrasty photos). `floor` keeps noise
 * on a featureless photo from being stretched into "edges".
 */
function gradients(src: Float32Array, w: number, h: number, percentile: number, floor: number): [Float32Array, Float32Array] {
  const gx = new Float32Array(w * h);
  const gy = new Float32Array(w * h);
  const mag = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const tl = src[i - w - 1], t = src[i - w], tr = src[i - w + 1];
      const l = src[i - 1], r = src[i + 1];
      const bl = src[i + w - 1], b = src[i + w], br = src[i + w + 1];
      const sx = tr + 2 * r + br - tl - 2 * l - bl;
      const sy = bl + 2 * b + br - tl - 2 * t - tr;
      gx[i] = sx;
      gy[i] = sy;
      mag[i] = Math.hypot(sx, sy);
    }
  }
  const ref = Math.max(floor, percentileOf(mag, percentile));
  for (let i = 0; i < w * h; i++) {
    const m = mag[i];
    if (m < 1e-6) continue;
    const s = Math.min(1.5, m / ref) / m;
    gx[i] *= s;
    gy[i] *= s;
  }
  return [gx, gy];
}

function percentileOf(values: Float32Array, q: number): number {
  // A strided sample is plenty for a normalization constant.
  const stride = Math.max(1, Math.floor(values.length / 20000));
  const sample: number[] = [];
  for (let i = 0; i < values.length; i += stride) sample.push(values[i]);
  sample.sort((a, b) => a - b);
  return sample[Math.min(sample.length - 1, Math.floor(sample.length * q))] ?? 0;
}
